import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { formatDistanceToNow } from 'date-fns';
import { useSnapshot } from 'valtio';
import { authStore } from '../../../../store/authStore';
import {
  useGetMyNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useDeleteNotificationMutation,
  INotificationFieldsFragment,
} from '../../../services/notificationService/__generated__/queries.generated';
import { NotificationType } from '../../../../__generated__/graphql';
import { getSiteProductKind } from '../../../constants/siteConfig';
import { notificationTarget } from './notificationTarget';

const Container = styled.div`
  ${tw`mx-auto w-full max-w-screen-xl min-w-0 box-border px-4 pt-6 pb-8 sm:p-6 sm:pb-8 md:pb-16`}
`;

const Header = styled.div`
  ${tw`mb-6 flex justify-between items-center`}
`;

const TitleSection = styled.div``;

const Title = styled.h1`
  ${tw`text-2xl font-bold text-text-primary`}
`;

const Subtitle = styled.p`
  ${tw`text-sm text-text-secondary mt-1`}
`;

const HeaderActions = styled.div`
  ${tw`flex gap-2`}
`;

const ActionButton = styled.button`
  ${tw`px-3 py-1.5 text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-secondary rounded-lg transition-colors`}
`;

const NotificationCard = styled.div<{ $isRead: boolean }>`
  ${tw`bg-white rounded-card p-4 mb-3 cursor-pointer border border-border-default max-w-full min-w-0 box-border`}
  ${props => !props.$isRead && tw`border-border-strong`}
  box-shadow: var(--shadow-card);
  transition:
    box-shadow var(--transition-normal),
    border-color var(--transition-normal);

  &:hover {
    box-shadow: var(--shadow-card-hover);
  }
`;

const NotificationHeader = styled.div`
  ${tw`flex justify-between items-start mb-2`}
`;

const NotificationTitle = styled.div<{ $isRead: boolean }>`
  ${tw`text-base font-semibold text-text-primary`}
  ${props => !props.$isRead && tw`text-text-primary`}
`;

const NotificationTime = styled.div`
  ${tw`text-xs text-text-secondary flex-shrink-0 ml-4`}
`;

const NotificationBody = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const NotificationType_ = styled.div`
  ${tw`text-xs text-text-tertiary mt-2 uppercase tracking-wide`}
`;

const UnreadDot = styled.div`
  ${tw`w-2 h-2 rounded-full bg-black flex-shrink-0 mr-3`}
`;

const ContentWrapper = styled.div`
  ${tw`flex items-start`}
`;

const TextContent = styled.div`
  ${tw`flex-1`}
`;

const EmptyState = styled.div`
  ${tw`py-16 text-center`}
`;

const EmptyTitle = styled.div`
  ${tw`text-lg font-semibold text-text-secondary mb-2`}
`;

const EmptySubtitle = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const LoadingWrapper = styled.div`
  ${tw`flex justify-center items-center py-16`}
`;

const LoadMoreButton = styled.button`
  ${tw`w-full py-3 text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-secondary rounded-lg transition-colors mt-4`}
`;

const getNotificationTypeLabel = (type: NotificationType): string => {
  const labels: Partial<Record<NotificationType, string>> = {
    [NotificationType.BookingCreated]: 'New Booking',
    [NotificationType.BookingConfirmed]: 'Booking Confirmed',
    [NotificationType.BookingModified]: 'Trip Modified',
    [NotificationType.BookingExtended]: 'Trip Extended',
    [NotificationType.BookingShortened]: 'Trip Shortened',
    [NotificationType.BookingCancelled]: 'Booking Cancelled',
    [NotificationType.RefundNeedsReview]: 'Refund Pending',
    [NotificationType.RefundCompleted]: 'Refund Complete',
    [NotificationType.RefundApproved]: 'Refund Approved',
    [NotificationType.ChatMessage]: 'New Message',
    [NotificationType.Broadcast]: 'Announcement',
    [NotificationType.CarSwapRequested]: 'Car Swap Requested',
    [NotificationType.CarSwapApproved]: 'Car Swap Approved',
    [NotificationType.CarSwapRejected]: 'Car Swap Rejected',
    [NotificationType.DepositAuthorized]: 'Deposit Authorized',
    [NotificationType.DepositCaptured]: 'Deposit Captured',
    [NotificationType.DepositReleased]: 'Deposit Released',
    [NotificationType.DamageClaimCreated]: 'Damage Claim Reported',
    [NotificationType.DamageClaimResponseSubmitted]: 'Claim Response',
    [NotificationType.DamageClaimApproved]: 'Claim Approved',
    [NotificationType.DamageClaimRejected]: 'Claim Rejected',
    [NotificationType.DamageClaimResolved]: 'Claim Resolved',
    // Spec 014c: the brand turned airport delivery off or back on.
    [NotificationType.BrandDeliveryOff]: 'Delivery Paused',
    [NotificationType.BrandDeliveryOn]: 'Delivery Back On',
    // Spec 015
    [NotificationType.CarUnlisted]: 'Car Unlisted',
    [NotificationType.CarRelisted]: 'Car Relisted',
    [NotificationType.TripCancelledRecall]: 'Trip Cancelled (Recall)',
    [NotificationType.RecallTripInProgress]: 'Safety Recall',
    [NotificationType.UnlistedCarFutureTrips]: 'Upcoming Trips Need Action',
    // Spec 013 P7: a card in another name was charged on a brand with the name check off.
    [NotificationType.CardholderNameMismatch]: 'Card Name Mismatch',
  };
  return labels[type] || type.replace(/_/g, ' ').toLowerCase();
};

interface NotificationsPageProps {
  role?: 'guest' | 'host';
  onRefetch?: () => void;
}

const NotificationsPage: React.FC<NotificationsPageProps> = ({ role, onRefetch }) => {
  const navigate = useNavigate();
  const auth = useSnapshot(authStore);
  const userId = auth.user?.id;
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, loading, error, refetch, fetchMore } = useGetMyNotificationsQuery({
    variables: { page: 1, limit, role },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const [markAsRead] = useMarkNotificationAsReadMutation();
  const [markAllAsRead] = useMarkAllNotificationsAsReadMutation();
  const [deleteNotification] = useDeleteNotificationMutation();

  const notifications = data?.myNotifications?.items || [];
  const hasMore = data?.myNotifications?.hasMore || false;
  const totalCount = data?.myNotifications?.totalCount || 0;

  const handleNotificationClick = useCallback(
    async (notification: INotificationFieldsFragment) => {
      // Mark as read
      if (!notification.isRead) {
        await markAsRead({ variables: { notificationId: notification.id } });
        refetch();
        onRefetch?.();
      }

      const target = notificationTarget(notification, role, getSiteProductKind());
      if (target) {
        navigate(target.path, target.state ? { state: target.state } : undefined);
      }
    },
    [markAsRead, navigate, refetch, onRefetch, role],
  );

  const handleMarkAllAsRead = useCallback(async () => {
    await markAllAsRead();
    refetch();
    onRefetch?.();
  }, [markAllAsRead, refetch, onRefetch]);

  const handleLoadMore = useCallback(async () => {
    const nextPage = page + 1;
    await fetchMore({
      variables: { page: nextPage, limit, role },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          ...prev,
          myNotifications: {
            ...fetchMoreResult.myNotifications,
            items: [
              ...(prev.myNotifications?.items || []),
              ...(fetchMoreResult.myNotifications?.items || []),
            ],
          },
        };
      },
    });
    setPage(nextPage);
  }, [fetchMore, page, limit, role]);

  const renderNotificationTime = (date?: string) => {
    if (!date) return '';
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  };

  if (!userId) {
    return (
      <Container>
        <EmptyState>
          <EmptyTitle>Please sign in</EmptyTitle>
          <EmptySubtitle>Sign in to view your notifications</EmptySubtitle>
        </EmptyState>
      </Container>
    );
  }

  if (loading && notifications.length === 0) {
    return (
      <Container>
        <LoadingWrapper>
          <div>Loading notifications...</div>
        </LoadingWrapper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <EmptyState>
          <EmptyTitle>Error</EmptyTitle>
          <EmptySubtitle>{error.message}</EmptySubtitle>
        </EmptyState>
      </Container>
    );
  }

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <Container>
      <Header>
        <TitleSection>
          <Title>Notifications</Title>
          <Subtitle>
            {totalCount > 0
              ? `${totalCount} notification${totalCount !== 1 ? 's' : ''}`
              : 'Stay updated on your bookings'}
          </Subtitle>
        </TitleSection>
        {unreadCount > 0 && (
          <HeaderActions>
            <ActionButton onClick={handleMarkAllAsRead}>Mark all as read</ActionButton>
          </HeaderActions>
        )}
      </Header>

      {notifications.length === 0 ? (
        <EmptyState>
          <EmptyTitle>No notifications yet</EmptyTitle>
          <EmptySubtitle>
            You'll receive notifications about your bookings, trips, and messages here.
          </EmptySubtitle>
        </EmptyState>
      ) : (
        <>
          {notifications.map(notification => (
            <NotificationCard
              key={notification.id}
              $isRead={notification.isRead}
              onClick={() => handleNotificationClick(notification)}
            >
              <ContentWrapper>
                {!notification.isRead && <UnreadDot />}
                <TextContent>
                  <NotificationHeader>
                    <NotificationTitle $isRead={notification.isRead}>
                      {notification.title}
                    </NotificationTitle>
                    <NotificationTime>
                      {renderNotificationTime(notification.createdAt)}
                    </NotificationTime>
                  </NotificationHeader>
                  <NotificationBody>{notification.body}</NotificationBody>
                  <NotificationType_>
                    {getNotificationTypeLabel(notification.type)}
                  </NotificationType_>
                </TextContent>
              </ContentWrapper>
            </NotificationCard>
          ))}

          {hasMore && (
            <LoadMoreButton onClick={handleLoadMore} disabled={loading}>
              {loading ? 'Loading...' : 'Load more'}
            </LoadMoreButton>
          )}
        </>
      )}
    </Container>
  );
};

export default NotificationsPage;
